/* ============================================================
   SECTION EDITOR — edit form (left) + a live, scaled preview of the
   real section (right). On mobile the form is primary with an
   Edit/Preview toggle. Draft/Publish/Discard live in the top bar.
   The preview renders the actual section components by feeding them
   the draft content through ContentContext.
   ============================================================ */
import { useLayoutEffect, useRef, useState } from 'react'
import { ContentContext, useContent, useSections } from '../content/ContentContext.jsx'
import { SECTION_COMPONENTS } from '../sections/index.js'
import { FIELD_SCHEMA, getPath } from '../content/schema.js'
import { Icon } from '../components/Icon.jsx'
import { MediaLibrary } from './MediaLibrary.jsx'
import { IconPicker } from './IconPicker.jsx'

/* ---------- live preview ----------
   Section CSS is responsive to the VIEWPORT width, so:
   - On a desktop viewport we render the section at 880px (its desktop
     layout) and scale it down to fit the preview pane.
   - On a mobile viewport we render at the real available width with no
     scaling, so the owner sees the genuine mobile layout — not a shrunk
     desktop one. */
const DESKTOP_WIDTH = 880
function LivePreview({ sectionKey, draft, media }) {
  const C = SECTION_COMPONENTS[sectionKey]
  const wrapRef = useRef(null)
  const frameRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [frameWidth, setFrameWidth] = useState(DESKTOP_WIDTH)
  const [natH, setNatH] = useState(800)

  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current
      const frame = frameRef.current
      if (!wrap || !frame) return
      const avail = wrap.clientWidth - 28
      // The public site switches to desktop layout at >=861px.
      const isMobileViewport = window.innerWidth < 861
      if (isMobileViewport) {
        setFrameWidth(avail) // true-to-life mobile width, no scaling
        setScale(1)
      } else {
        setFrameWidth(DESKTOP_WIDTH)
        setScale(Math.min(1, Math.max(0.25, avail / DESKTOP_WIDTH)))
      }
      setNatH(frame.offsetHeight)
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (wrapRef.current) ro.observe(wrapRef.current)
    if (frameRef.current) ro.observe(frameRef.current)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  })

  return (
    <div className="preview-scaler" ref={wrapRef}>
      <div style={{ position: 'relative', height: natH * scale }}>
        <div
          className="preview-frame"
          ref={frameRef}
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            width: frameWidth,
            transform: `translateX(-50%) scale(${scale})`,
            transformOrigin: 'top center',
          }}
        >
          <ContentContext.Provider value={{ content: draft, media }}>
            <C />
          </ContentContext.Provider>
        </div>
      </div>
    </div>
  )
}

/* ---------- field controls ---------- */
function TextField({ path, label, area, draft, setDraftPath }) {
  const val = getPath(draft, path) ?? ''
  return (
    <div className="ff">
      <label>{label}</label>
      {area ? (
        <textarea value={val} onChange={(e) => setDraftPath(path, e.target.value)} />
      ) : (
        <input value={val} onChange={(e) => setDraftPath(path, e.target.value)} />
      )}
    </div>
  )
}

function mediaBg(m) {
  if (!m) return '#100f0d'
  return m.url ? `center / cover no-repeat url(${m.url})` : m.css || '#100f0d'
}

function ImageField({ path, label, draft, media, onOpenMedia }) {
  const id = getPath(draft, path)
  const m = media.find((x) => x.id === id)
  return (
    <div className="ff">
      <label>{label}</label>
      <div className="slot-edit">
        <div className="slot-thumb">
          <div style={{ position: 'absolute', inset: 0, background: mediaBg(m) }}></div>
          {!m?.url && <div className="ph"></div>}
        </div>
        <div>
          <div
            style={{
              fontFamily: 'var(--ff-mono)',
              fontSize: 11,
              color: 'var(--body)',
              marginBottom: 8,
            }}
          >
            {m ? m.name : '—'}
          </div>
          <button className="btn-ghost" onClick={() => onOpenMedia(path)}>
            Replace image
          </button>
        </div>
      </div>
    </div>
  )
}

function IconField({ label, name, path, onOpenIcons }) {
  return (
    <div className="ff">
      <label>{label}</label>
      <div className="slot-edit">
        <div className="icon-slot-thumb">
          <Icon name={name} size={28} />
        </div>
        <div>
          <div
            style={{
              fontFamily: 'var(--ff-mono)',
              fontSize: 11,
              color: 'var(--body)',
              marginBottom: 8,
            }}
          >
            {name}
          </div>
          <button className="btn-ghost" onClick={() => onOpenIcons(path)}>
            Change icon
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------- special editors ---------- */
function DisciplinesEditor({ draft, setDraftPath, onOpenIcons }) {
  return (
    <div className="fgroup">
      <div className="fgroup-h">Register — five disciplines</div>
      {draft.disciplines.blocks.map((b, i) => (
        <div className="block-edit" key={b.idx} style={{ marginBottom: 22 }}>
          <div
            className="be-head"
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}
          >
            <div
              className="be-idx"
              style={{ fontFamily: 'var(--ff-mono)', color: 'var(--sage)', fontSize: 12 }}
            >
              {b.idx}
            </div>
            <input
              value={b.title}
              onChange={(e) => setDraftPath('disciplines.blocks.' + i + '.title', e.target.value)}
              style={{
                flex: 1,
                background: 'var(--panel-2)',
                border: '1px solid var(--line-strong)',
                borderRadius: 5,
                padding: '8px 10px',
                color: 'var(--bone)',
                fontSize: 14,
              }}
            />
            <button
              className="icon-chip"
              title="Change icon"
              onClick={() => onOpenIcons('disciplines.blocks.' + i + '.icon')}
            >
              <Icon name={b.icon} size={22} />
            </button>
          </div>
          <div className="ff">
            <label>Intro line</label>
            <input
              value={b.intro}
              onChange={(e) => setDraftPath('disciplines.blocks.' + i + '.intro', e.target.value)}
            />
          </div>
          <div className="ff">
            <label>Serif keywords</label>
            <textarea
              value={b.keywords}
              onChange={(e) =>
                setDraftPath('disciplines.blocks.' + i + '.keywords', e.target.value)
              }
            />
          </div>
          <div className="ff">
            <label>Closing line (sage)</label>
            <input
              value={b.closing}
              onChange={(e) => setDraftPath('disciplines.blocks.' + i + '.closing', e.target.value)}
            />
          </div>
          <details>
            <summary
              style={{
                cursor: 'pointer',
                fontFamily: 'var(--ff-mono)',
                fontSize: 10,
                letterSpacing: '.1em',
                color: 'var(--sage)',
                margin: '6px 0 12px',
              }}
            >
              FOUR SUB-ITEMS
            </summary>
            {b.items.map((it, j) => (
              <div
                key={it.code}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}
              >
                <input
                  value={it.title}
                  onChange={(e) =>
                    setDraftPath(
                      'disciplines.blocks.' + i + '.items.' + j + '.title',
                      e.target.value,
                    )
                  }
                  style={inputSm}
                />
                <input
                  value={it.tag}
                  onChange={(e) =>
                    setDraftPath('disciplines.blocks.' + i + '.items.' + j + '.tag', e.target.value)
                  }
                  style={inputSm}
                />
                <input
                  value={it.note}
                  onChange={(e) =>
                    setDraftPath(
                      'disciplines.blocks.' + i + '.items.' + j + '.note',
                      e.target.value,
                    )
                  }
                  style={{
                    ...inputSm,
                    gridColumn: '1/-1',
                    fontStyle: 'italic',
                    fontFamily: 'var(--ff-serif)',
                  }}
                />
              </div>
            ))}
          </details>
        </div>
      ))}
    </div>
  )
}

const inputSm = {
  background: 'var(--panel-2)',
  border: '1px solid var(--line)',
  borderRadius: 5,
  padding: '8px',
  color: 'var(--bone)',
  fontSize: 13,
}

function ScenesEditor({ draft, setDraftPath, media, onOpenMedia }) {
  return (
    <div style={{ marginTop: 12 }}>
      {draft.method.scenes.map((s, i) => {
        const m = media.find((x) => x.id === s.media)
        return (
          <div
            className="scene-edit"
            key={s.id}
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}
          >
            <div className="slot-thumb" style={{ width: 56, height: 42 }}>
              <div style={{ position: 'absolute', inset: 0, background: mediaBg(m) }}></div>
            </div>
            <input
              value={s.label}
              onChange={(e) => setDraftPath('method.scenes.' + i + '.label', e.target.value)}
              style={{
                flex: 1,
                background: 'var(--panel-2)',
                border: '1px solid var(--line-strong)',
                borderRadius: 5,
                padding: '8px 10px',
                color: 'var(--bone)',
                fontSize: 13,
                fontFamily: 'var(--ff-mono)',
                letterSpacing: '.08em',
              }}
            />
            <button
              className="btn-ghost"
              onClick={() => onOpenMedia('method.scenes.' + i + '.media')}
            >
              Image
            </button>
          </div>
        )
      })}
    </div>
  )
}

function EditFormFields({ sectionKey, draft, media, setDraftPath, onOpenMedia, onOpenIcons }) {
  const schema = FIELD_SCHEMA[sectionKey] || []
  return (
    <>
      {schema.map((g, gi) => (
        <div className="fgroup" key={gi}>
          <div className="fgroup-h">{g.group}</div>
          {g.fields.map((f, fi) => {
            if (f.t === 'image')
              return (
                <ImageField
                  key={fi}
                  path={f.p}
                  label={f.l}
                  draft={draft}
                  media={media}
                  onOpenMedia={onOpenMedia}
                />
              )
            if (f.t === 'icon')
              return (
                <IconField
                  key={fi}
                  path={f.p}
                  label={f.l}
                  name={getPath(draft, f.p)}
                  onOpenIcons={onOpenIcons}
                />
              )
            return (
              <TextField
                key={fi}
                path={f.p}
                label={f.l}
                area={f.t === 'area'}
                draft={draft}
                setDraftPath={setDraftPath}
              />
            )
          })}
          {g.scenes && (
            <ScenesEditor
              draft={draft}
              setDraftPath={setDraftPath}
              media={media}
              onOpenMedia={onOpenMedia}
            />
          )}
        </div>
      ))}
      {sectionKey === 'disciplines' && (
        <DisciplinesEditor draft={draft} setDraftPath={setDraftPath} onOpenIcons={onOpenIcons} />
      )}
    </>
  )
}

/* ---------- editor shell ---------- */
export function SectionEditor({ sectionKey, drafts, onBack }) {
  const { media } = useContent()
  const { draft, meta, saving, setDraftPath, publish, discard } = drafts
  const [mobilePreview, setMobilePreview] = useState(false)
  // picker: null | { kind:'media'|'icon', path }
  const [picker, setPicker] = useState(null)
  const sections = useSections()
  const sec = sections.find((s) => s.key === sectionKey) || { num: '', label: sectionKey }
  const status = (meta[sectionKey] && meta[sectionKey].status) || 'Published'

  const openMedia = (path) => setPicker({ kind: 'media', path })
  const openIcons = (path) => setPicker({ kind: 'icon', path })
  const pick = (value) => {
    if (picker) setDraftPath(picker.path, value)
    setPicker(null)
  }

  if (!draft) return <div className="empty">Loading…</div>

  return (
    <div className="editor">
      <div className="editor-bar">
        <button className="back" onClick={onBack}>
          <Icon name="back" size={15} /> DASHBOARD
        </button>
        <div style={{ marginLeft: 6 }}>
          <div className="crumb">EDITING</div>
          <div style={{ color: 'var(--bone)', fontSize: 15, fontWeight: 500 }}>
            {sec.num} · {sec.label}
          </div>
        </div>
        <div className="spacer"></div>

        <div className={'status ' + (status === 'Published' ? 'pub' : 'draft')}>
          {saving ? 'Saving…' : status}
        </div>

        <div className="toggle mob-only">
          <button className={!mobilePreview ? 'on' : ''} onClick={() => setMobilePreview(false)}>
            Edit
          </button>
          <button className={mobilePreview ? 'on' : ''} onClick={() => setMobilePreview(true)}>
            Preview
          </button>
        </div>

        <button className="btn-ghost" onClick={() => discard(sectionKey)}>
          Discard
        </button>
        <button className="btn-pub" onClick={() => publish(sectionKey)}>
          Publish
        </button>
      </div>

      <div className="editor-split">
        <div className={'edit-form' + (mobilePreview ? ' hidden-mobile' : '')}>
          <EditFormFields
            sectionKey={sectionKey}
            draft={draft}
            media={media}
            setDraftPath={setDraftPath}
            onOpenMedia={openMedia}
            onOpenIcons={openIcons}
          />
        </div>
        <div className={'preview-pane' + (mobilePreview ? '' : ' hidden-mobile')}>
          <div className="pv-label">LIVE PREVIEW — {status.toUpperCase()}</div>
          <LivePreview sectionKey={sectionKey} draft={draft} media={media} />
        </div>
      </div>

      {picker && (
        <div className="picker-overlay" role="dialog" aria-modal="true">
          <div className="picker-panel">
            <div className="picker-head">
              <div className="crumb">{picker.kind === 'media' ? 'MEDIA LIBRARY' : 'ICON SET'}</div>
              <button className="oclose" onClick={() => setPicker(null)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="picker-body">
              {picker.kind === 'media' ? (
                <MediaLibrary picking onPick={pick} />
              ) : (
                <IconPicker current={getPath(draft, picker.path)} onPick={pick} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
